import { RestClient } from './RestClient'
import store from 'src/store'

export class PersonClient extends RestClient {
  /**
   * PersonClient constructor
   *
   * @constructor
   */
  constructor () {
    super()

    let currentTeam = store.getters.activeTeam
    this.baseUrl = `/teams/${currentTeam.id}/persons`
  }

  /**
   * Get a list of persons
   *
   * @returns {AxiosPromise}
   */
  list () {
    return this.call('get', this.baseUrl)
  }

  /**
   * Show the specified persons
   *
   * @param {(...number|number[])} id - Persons to show
   *
   * @returns {AxiosPromise}
   */
  get (...id) {
    return this.call('get', `${this.baseUrl}/${this.parseAndJoin(id)}`)
  }

  /**
   * Store a new person
   *
   * @param {Object} data - person info
   *
   * @returns {AxiosPromise}
   */
  create (data) {
    return this.call('post', this.baseUrl, data)
  }

  /**
   * Update an existing person
   *
   * @param {number} personId - Person id
   * @param {Object} data - Person info
   *
   * @returns {AxiosPromise}
   */
  update (personId, data) {
    return this.call('patch', `${this.baseUrl}/${personId}`, data)
  }

  /**
   * Delete the specified Persons
   *
   * @param {(...number|number[])} personId - Persons which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroy (...personId) {
    return this.call('delete', `${this.baseUrl}/${this.parseAndJoin(personId)}`)
  }

  /**
   * Create a new address for the specified person
   *
   * @param {number} personId - Person id
   * @param {Object} data - Address info
   *
   * @returns {AxiosPromise}
   */
  createAddress (personId, data) {
    return this.call('post', `${this.baseUrl}/${personId}/addresses`, data)
  }

  /**
   * Update an existing person address
   *
   * @param {number} personId - Person id
   * @param {number} addressId - Address id
   * @param {Object} data - Address info
   *
   * @returns {AxiosPromise}
   */
  updateAddress (personId, addressId, data) {
    return this.call('patch', `${this.baseUrl}/${personId}/addresses/${addressId}`, data)
  }

  /**
   * Delete the specified person addresses
   *
   * @param {number} personId - Person id
   * @param {(...number|number[])} addressId - Addresses which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyAddresses (personId, ...addressId) {
    return this.call('delete', `${this.baseUrl}/${personId}/addresses/${this.parseAndJoin(addressId)}`)
  }

  /**
   * Create a new email address for the specified person
   *
   * @param {number} personId - Person id
   * @param {Object} data - Email address info
   *
   * @returns {AxiosPromise}
   */
  createEmailAddress (personId, data) {
    return this.call('post', `${this.baseUrl}/${personId}/email-addresses`, data)
  }

  /**
   * Update an existing person email address
   *
   * @param {number} personId - Person id
   * @param {number} emailAddressId - Email address id
   * @param {Object} data - Email address info
   *
   * @returns {AxiosPromise}
   */
  updateEmailAddress (personId, emailAddressId, data) {
    return this.call('patch', `${this.baseUrl}/${personId}/email-addresses/${emailAddressId}`, data)
  }

  /**
   * Delete the specified person email addresses
   *
   * @param {number} personId - person id
   * @param {(...number|number[])} emailAddressId - Email addresses which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyEmailAddresses (personId, ...emailAddressId) {
    return this.call('delete', `${this.baseUrl}/${personId}/email-addresses/${this.parseAndJoin(emailAddressId)}`)
  }

  /**
   * Create a new phone number for the specified person
   *
   * @param {number} personId - Person id
   * @param {Object} data - Phone number info
   *
   * @returns {AxiosPromise}
   */
  createPhoneNumber (personId, data) {
    return this.call('post', `${this.baseUrl}/${personId}/phone-numbers`, data)
  }

  /**
   * Update an existing person phone number
   *
   * @param {number} personId - Person id
   * @param {number} phoneNumberId - Phone number id
   * @param {Object} data - Phone number info
   *
   * @returns {AxiosPromise}
   */
  updatePhoneNumber (personId, phoneNumberId, data) {
    return this.call('patch', `${this.baseUrl}/${personId}/phone-numbers/${phoneNumberId}`, data)
  }

  /**
   * Delete the specified person phone numbers
   *
   * @param {number} personId - Person id
   * @param {(...number|number[])} phoneNumberId - Phone numbers which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyPhoneNumbers (personId, ...phoneNumberId) {
    return this.call('delete', `${this.baseUrl}/${personId}/phone-numbers/${this.parseAndJoin(phoneNumberId)}`)
  }

  /**
   * Create a new note for the specified person
   *
   * @param {number} personId - Person id
   * @param {Object} data - Note info
   *
   * @returns {AxiosPromise}
   */
  createNote (personId, data) {
    return this.call('post', `${this.baseUrl}/${personId}/notes`, data)
  }

  /**
   * Create a new bank details for the specified person
   *
   * @param {number} personId - Person id
   * @param {Object} data - Bank details info
   *
   * @returns {AxiosPromise}
   */
  createBankDetails (personId, data) {
    return this.call('post', `${this.baseUrl}/${personId}/bank-details`, data)
  }

  /**
   * Update an existing person bank details
   *
   * @param {number} personId - Person id
   * @param {number} bankDetailsId - Bank details id
   * @param {Object} data - Bank details info
   *
   * @returns {AxiosPromise}
   */
  updateBankDetails (personId, bankDetailsId, data) {
    return this.call('patch', `${this.baseUrl}/${personId}/bank-details/${bankDetailsId}`, data)
  }

  /**
   * Delete the specified person bank details
   *
   * @param {number} personId - Person id
   * @param {(...number|number[])} bankDetailsId - Bank details which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyBankDetails (personId, ...bankDetailsId) {
    return this.call('delete', `${this.baseUrl}/${personId}/bank-details/${this.parseAndJoin(bankDetailsId)}`)
  }

  /**
   * Update the image of the specified person
   *
   * @param {number} personId - Person id
   * @param {File} logo - New person image
   *
   * @returns {AxiosPromise}
   */
  uploadLogo (personId, logo) {
    let data = new FormData()
    data.append('image', logo)

    return this.call('post', `${this.baseUrl}/${personId}/image`, data)
  }

  /**
   * Delete the image of the specified person
   *
   * @param {number} personId - Person id
   *
   * @returns {AxiosPromise}
   */
  deleteImage (personId) {
    return this.call('delete', `${this.baseUrl}/${personId}/image`)
  }
}
